import { useState, useEffect } from "react";
import Modal from "@modalSrc/Modal";
import { toast } from "react-toastify";
import RightTimeApi from "@/API";
import ModalFormFields from "@modalSrc/ModalFormFields";
import { MdDelete } from "react-icons/md";
import ConfirmModal from "@elements/modals/ConfirmModal";
import DuplicateTestTypeNameModal from "@elements/modals/DuplicateTestTypeNameModal";
import Enums from "@/enums";
import { apiMessage } from "@/utils/apiMessage";

type FormErrors = Record<string, string | null>;

type TestTypeFormData = {
    id?: number;
    name?: string;
    evalComparison?: number;
    restartable?: number;
};

export default function TestTypeModal({ testTypeId, onClose, onDuplicated, onDeleted, updateSpinner }) {
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [isDuplicating, setIsDuplicating] = useState(false);
    const nestedModalOpen = showDeleteConfirm || showDuplicateModal;

    const [formData, setFormData] = useState<TestTypeFormData>({});
    const [errors, setErrors] = useState<FormErrors>({});
    const [originalTestType, setOriginalTestType] = useState(null);
    const [testTypeData, setTestTypeData] = useState(null);

    useEffect(() => {
        if (testTypeId && testTypeId > 0) {
            fetchTestType();
        } else if (testTypeId !== null && testTypeId !== undefined && testTypeId <= 0) {
            // New test type
            setTestTypeData(null);
            setOriginalTestType(null);
            setFormData({});
            setErrors({});
        }
    }, [testTypeId]);

    useEffect(() => {
        if (testTypeId && testTypeData) {
            setOriginalTestType(testTypeData);
            setFormData(testTypeData || {});
        }
    }, [testTypeId, testTypeData]);

    async function fetchTestType() {
        if (!testTypeId || testTypeId <= 0) return;
        
        updateSpinner && updateSpinner(true);
        try {
            const resp = await RightTimeApi.getAsync(`/TestType/${testTypeId}`);
            if (resp && resp.ok) {
                setTestTypeData(resp.jsonData || resp.data);
            } else {
                toast.error("Failed to load test type data.");
                setTestTypeData(null);
            }
        } catch (error) {
            console.error("Error fetching test type:", error);
            toast.error("Error loading test type data.");
            setTestTypeData(null);
        } finally {
            updateSpinner && updateSpinner(false);
        }
    }

    if (!testTypeId && testTypeId !== 0) return null;

    async function handleDelete() {
        setShowDeleteConfirm(false);
        const deleteId = originalTestType?.id ?? testTypeId;
        if (!deleteId || deleteId <= 0) {
            toast.error("No test type selected to delete.");
            return;
        }
        if (originalTestType?.canDelete === false) {
            toast.error("Cannot delete this test type because it is in use.");
            return;
        }

        updateSpinner && updateSpinner(true);
        try {
            const resp = await RightTimeApi.deleteAsync(`/TestType/${deleteId}`);
            if (resp && resp.ok) {
                toast.success(resp.jsonData?.message || "Test type deleted");
                if (onDeleted) {
                    onDeleted(deleteId);
                }
                onClose && onClose(true);
            } else {
                const message = apiMessage(resp, "Failed to delete test type.");
                toast.error(message);
            }
        } catch (error) {
            toast.error(error.message || "Error deleting test type.");
        } finally {
            updateSpinner && updateSpinner(false);
        }
    }

    function validateForm() {
        const newErrors: FormErrors = {};
        if (!formData.name || !formData.name.trim()) {
            newErrors.name = "Test type name is required";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    function isChanged() {
        if (!formData) return false;
        if (originalTestType && originalTestType.id && originalTestType.id > 0) {
            return formData.name !== originalTestType.name ||
                   formData.evalComparison !== (originalTestType.evalComparison || 0) ||
                   Number(formData.restartable || 0) !== Number(originalTestType.restartable || 0);
        } else {
            return Boolean(formData.name?.trim()) || Boolean(formData.evalComparison) || Number(formData.restartable || 0) !== 0;
        }
    }

    async function handleSave() {
        if (!validateForm() || !isChanged()) return;
        updateSpinner && updateSpinner(true);
        try {
            let resp = await RightTimeApi.sendAsync("/TestType", formData);
            if (resp && resp.ok) {
                onClose && onClose(true);
            } else {
                toast.error("Failed to save test type.");
            }
        } catch (error) {
            toast.error(error.message || "Error saving test type.");
        } finally {
            updateSpinner && updateSpinner(false);
        }
    }

    function handleCancel() {
        setFormData({});
        setErrors({});
        setTestTypeData(null);
        setOriginalTestType(null);
        onClose && onClose(false);
    }

    function handleModalClose() {
        if (nestedModalOpen) return;
        handleCancel();
    }

    const canDelete = originalTestType?.canDelete !== false;

    async function handleDuplicate(newName) {
        if (!originalTestType?.id) return;
        setIsDuplicating(true);
        try {
            const resp = await RightTimeApi.postAsync(
                `/TestType/${originalTestType.id}/Duplicate`,
                { name: newName }
            );
            if (resp?.ok && resp.jsonData?.id) {
                toast.success("Test type duplicated successfully");
                setShowDuplicateModal(false);
                if (onDuplicated) {
                    await onDuplicated(resp.jsonData.id);
                }
                onClose && onClose(true);
                return;
            }

            const message = apiMessage(resp, "Failed to duplicate test type");
            toast.error(message);
        } catch (error) {
            console.error("Error duplicating test type:", error);
            toast.error("An error occurred while duplicating the test type");
        } finally {
            setIsDuplicating(false);
        }
    }

    function handleInputChange(field, value) {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    }

    const fields = {
        "Test Type Name *": <input id="testTypeName" type="text" value={formData?.name || ''} onChange={e => handleInputChange('name', e.target.value)} placeholder="Enter test type name" className={errors.name ? 'error' : ''} />,
        ...(errors.name && { '': <span className="errorText">{errors.name}</span> }),
        "EvalType": (
            <select 
                value={formData?.evalComparison || 0}
                onChange={e => handleInputChange('evalComparison', parseInt(e.target.value))}
            >
                <option value={Enums.TEST_EVAL_COMPARISON.None}>None</option>
                <option value={Enums.TEST_EVAL_COMPARISON.Culture}>Culture</option>
                <option value={Enums.TEST_EVAL_COMPARISON.EF}>{Enums.getTestEvalComparisonLabel(Enums.TEST_EVAL_COMPARISON.EF)}</option>
                <option value={Enums.TEST_EVAL_COMPARISON.BE_SOURCE}>{Enums.getTestEvalComparisonLabel(Enums.TEST_EVAL_COMPARISON.BE_SOURCE)}</option>
            </select>
        ),
        "Restart": (
            <select
                aria-label="Restart mode"
                value={Number(formData?.restartable) || 0}
                onChange={e => handleInputChange('restartable', parseInt(e.target.value, 10))}
                title="Default copied to new company test types. Not restartable starts a new test. Section restart reopens the unfinished section. Answer restart restores saved answers and page."
            >
                <option value={Enums.TEST_RESTART_MODE.NotRestartable}>Not restartable</option>
                <option value={Enums.TEST_RESTART_MODE.SectionRestart}>Section restart</option>
                <option value={Enums.TEST_RESTART_MODE.AnswerRestart}>Answer restart</option>
            </select>
        )
    };

    const footer = (
        <>
            <div className="footerLeft">
                {originalTestType && originalTestType.id > 0 && (
                    <>
                        <button
                            type="button"
                            className="btn btn-info"
                            onClick={() => setShowDuplicateModal(true)}
                            disabled={isDuplicating}
                            title={isDuplicating ? "Duplicating..." : "Duplicate Test Type"}
                            data-help-id="DuplicateTestType"
                        >
                            {isDuplicating ? "Duplicating..." : "Duplicate"}
                        </button>
                        <button
                            type="button"
                            className="btn btn-danger iconButton"
                            onClick={() => setShowDeleteConfirm(true)}
                            disabled={!canDelete}
                            title={canDelete ? "Delete Test Type" : "Cannot delete: test type is in use"}
                        >
                            <MdDelete />
                        </button>
                    </>
                )}
            </div>
            <div className="footerRight">
                <button
                    type="button"
                    className={`btn btn-primary${!isChanged() || !formData?.name?.trim() ? ' disabled' : ''}`}
                    onClick={handleSave}
                    disabled={!isChanged() || !formData?.name?.trim()}
                >
                    Save
                </button>
            </div>
        </>
    );

    return (
        <>
            <Modal
                isOpen={testTypeId !== null && testTypeId !== undefined}
                title={originalTestType && originalTestType.id ? 'Edit Test Type' : 'Add New Test Type'}
                onClose={handleModalClose}
                footer={footer}
                className="testTypeModal"
            >
                <div className="modalBody">
                    <ModalFormFields fields={fields} />
                </div>
            </Modal>
            <ConfirmModal
                isOpen={showDeleteConfirm}
                onConfirm={handleDelete}
                onCancel={() => setShowDeleteConfirm(false)}
                title="Delete Test Type"
                message={`Are you sure you want to delete the test type${originalTestType?.name ? ` "${originalTestType.name}"` : ''}?`}
                confirmText="Yes, Delete Test Type"
                dangerText="This will permanently remove the test type and all of its master form fields and sections."
                zIndex={1100}
            />
            <DuplicateTestTypeNameModal
                isOpen={showDuplicateModal}
                onClose={() => setShowDuplicateModal(false)}
                onDuplicate={handleDuplicate}
                isDuplicating={isDuplicating}
                zIndex={1100}
            />
        </>
    );
}


